import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Ticket, CheckCircle, Clock, MessageSquare, FileCode, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = { open: "#a1a1aa", triaging: "#a78bfa", in_progress: "#60a5fa", resolved: "#34d399", failed: "#f87171", closed: "#71717a" };
const TYPE_COLORS = { bug: "#f87171", incident: "#fb923c", feature_request: "#60a5fa", question: "#22d3ee", feedback: "#a78bfa" };
const PRIORITY_COLORS = { critical: "#ef4444", high: "#f97316", medium: "#eab308", low: "#71717a" };
const PIE_COLORS = ["#60a5fa", "#34d399", "#a78bfa", "#fb923c", "#22d3ee", "#f87171", "#eab308"];

function StatCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase mb-1">
          <Icon className={`w-4 h-4 ${color}`} /> {label}
        </div>
        <p className="text-2xl font-bold font-mono text-white">{value}</p>
        {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card className="bg-[#0c0c0e] border-white/5">
      <CardContent className="p-4">
        <p className="text-sm text-zinc-400 mb-3">{title}</p>
        {children}
      </CardContent>
    </Card>
  );
}

const tooltipStyle = { backgroundColor: "#18181b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 };

const toChartData = (obj, colorMap) =>
  Object.entries(obj || {}).map(([name, value], i) => ({
    name: name.replace("_", " "), value,
    fill: (colorMap && colorMap[name]) || PIE_COLORS[i % PIE_COLORS.length],
  }));

export const HelpdeskAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/it-tickets/analytics`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>;
  if (!data) return <p className="text-zinc-500 text-sm py-8 text-center">Failed to load analytics.</p>;

  const systems = toChartData(data.by_system);
  const statuses = toChartData(data.by_status, STATUS_COLORS);
  const types = toChartData(data.by_type, TYPE_COLORS);
  const priorities = toChartData(data.by_priority, PRIORITY_COLORS);

  return (
    <div className="space-y-4" data-testid="helpdesk-analytics-panel">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard icon={Ticket} label="Total" value={data.total} color="text-blue-400" testid="stat-total" />
        <StatCard icon={Clock} label="Open" value={data.open} color="text-yellow-400" testid="stat-open" />
        <StatCard icon={CheckCircle} label="Resolved" value={data.resolved} sub={`${data.resolution_rate}% rate`} color="text-green-400" testid="stat-resolved" />
        <StatCard icon={TrendingUp} label="Avg Resolve" value={data.avg_resolve_hours != null ? `${data.avg_resolve_hours}h` : "—"} color="text-cyan-400" testid="stat-avg-resolve" />
        <StatCard icon={MessageSquare} label="Replies Pending" value={data.replies_pending} color="text-purple-400" testid="stat-replies-pending" />
        <StatCard icon={FileCode} label="Fix Tickets" value={data.fix_tickets_generated} color="text-emerald-400" testid="stat-fix-tickets" />
      </div>

      {/* Trend */}
      <ChartCard title="14-day trend — created vs resolved">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={data.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={d => d.slice(5)} />
            <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} width={28} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="created" stroke="#60a5fa" fill="#60a5fa22" name="Created" />
            <Area type="monotone" dataKey="resolved" stroke="#34d399" fill="#34d39922" name="Resolved" />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Tickets by system">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={systems} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 11 }} width={110} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Status breakdown">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statuses} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tickets by type">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={types} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Priority distribution">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={priorities}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} width={28} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};
